import { createContext, useContext, useEffect, useState } from 'react';
import { authApi } from '@/lib/api';

interface User {
  id: string;
  email: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    // Verificar usuario atual
    const checkUser = async () => {
      try {
        const currentUser = await authApi.getCurrentUser();
        if (currentUser) {
          setUser({
            id: currentUser.id,
            email: currentUser.email || '',
            role: currentUser.user_metadata?.role || 'admin'
          });
        }
      } catch (error) {
        // Silently handle auth errors - user just isnt logged in
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Setup auth state listener
    const setupAuthListener = async () => {
      try {
        const { data } = await authApi.onAuthStateChange((user) => {
          if (user) {
            setUser({
              id: user.id,
              email: user.email || '',
              role: user.user_metadata?.role || 'admin'
            });
          } else {
            setUser(null);
          }
          setLoading(false);
        });
        subscription = data?.subscription || null;
      } catch (error) {
        // Supabase not available - continue without auth listener
        console.warn('Auth listener not available');
      }
    };

    checkUser();
    setupAuthListener();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { user: authUser } = await authApi.signIn(email, password);
      if (authUser) {
        setUser({
          id: authUser.id,
          email: authUser.email || '',
          role: authUser.user_metadata?.role || 'admin'
        });
      }
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await authApi.signOut();
      setUser(null);
    } catch (error) {
      console.error('Erro no logout:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
